/**
 * Class containing route paths of the views.
 */
export default class Routes {
	/**
	 * Route for contacts List.
	 */
	static ContactsList = "/app/contacts";
	/**
	 * Route for sites List.
	 */
	static SitesList = "/app/sites";
	/**
	 * Route for leads List.
	 */
	static LeadsList = "/app/leads";
	/**
	 * Route for jobs List.
	 */
	static JobsList = "/app/jobs";
	/**
	 * Route to Sign In page.
	 */
	static SignIn = "/auth/signin";
	/**
	 * Route to Reset Password page.
	 */
	static ResetPassword = "/auth/reset-password";
	/**
	 * Route to Forgot Password page.
	 */
	static ForgotPassword = "/auth/forgot-password";
	/**
	 * Route for create lead.
	 * @type {string}
	 */
	static CreateLead = "/app/leads/create";
	/**
	 * Route for create lead.
	 * @type {string}
	 */
	static CreateJob = "/app/jobs/create";
	/**
	 * Route for entering an electricity bill of a meter dedicated to EV charging as a job.
	 */
	static CreateEvChargingJob = "/app/jobs/ev-charging/create";
	/**
	 * Route for the quarters of EV charging bills, each a Statement of Supply for NORA.
	 */
	static EvChargingQuarters = "/app/accounting/rtfo_submissions";
	/**
	 * Route for one quarter of EV charging bills, by meter.
	 * @param {string} quarterId Quarter identifier.
	 */
	static EvChargingQuarter = (quarterId: string) => `/app/accounting/rtfo_submissions/${quarterId}`;
	/**
	 * Route for edit lead.
	 * @param {string} leadReferenceNumber Lead identifier.
	 */
	static EditLead = (leadReferenceNumber: string) => `/app/leads/${leadReferenceNumber}`;
	/**
	 * Route for creating lead quote.
	 * @param {string} leadId Lead identifier.
	 */
	static CreateLeadQuote = (leadId: string) => `/app/leads/${leadId}/quote`;
	/**
	 * Route for edit job.
	 * @param {string} jobReferenceNumber Job identifier.
	 */
	static EditJob = (jobReferenceNumber: string) => `/app/jobs/${jobReferenceNumber}`;
	/**
	 * Route for edit contact.
	 * @param {string} contactId Contact identifier.
	 */
	static EditContact = (contactId: string) => `/app/contacts/${contactId}`;
	/**
	 * Route for brands list.
	 */
	static BrandList = "/app/admin/brands";
	/**
	 * Route for brand creation.
	 */
	static BrandCreate = "/app/admin/brands/create";
	/**
	 * Route for brand editing
	 * @param brandId
	 */
	static BrandEdit = (brandId: string) => `/app/admin/brands/${brandId}/edit`;
	/**
	 * Route for partner brands list.
	 */
	static PartnerBrandList = "/app/admin/partnerbrands";
	/**
	 * Route for partner brand creation.
	 */
	static PartnerBrandCreate = "/app/admin/partnerbrands/create";
	/**
	 * Route for partner brand editing
	 * @param partnerBrandId
	 */
	static PartnerBrandEdit = (partnerBrandId: string) => `/app/admin/partnerbrands/${partnerBrandId}`;
	/**
	 * Route to survey activity templates tasks
	 * @param activityId
	 * @constructor
	 */
	static ActivityTemplateTasks = (activityId: string) => `/app/admin/activity/${activityId}`;
	/**
	 * Route to job activity templates tasks
	 * @param activityId
	 * @constructor
	 */
	static JobActivityTemplateTasks = (activityId: string) => `/app/admin/jobactivity/${activityId}`;
	/**
	 * Route to quote details related with lead
	 * @param {string} leadReferenceNumber
	 * @param {string} quoteId
	 * @returns {string}
	 * @constructor
	 */
	static LeadQuoteView = (leadReferenceNumber: string, quoteId: string) =>
		`/app/leads/${leadReferenceNumber}/quotes/${quoteId}`;

	/**
	 * Route to quote details related with lead
	 * @param {string} leadReferenceNumber
	 * @param {string} quoteId
	 * @param versionNumber
	 * @returns {string}
	 * @constructor
	 */
	static LeadQuoteHistoricalView = (leadReferenceNumber: string, quoteId: string, versionNumber: number) =>
		`/app/leads/${leadReferenceNumber}/quotes/${quoteId}/${versionNumber}`;
	/**
	 * Route to quote details related with job
	 * @param {string} jobReferenceNumber
	 * @param {string} quoteId
	 * @returns {string}
	 * @constructor
	 */
	static JobQuoteView = (jobReferenceNumber: string, quoteId: string) => `/app/jobs/${jobReferenceNumber}/quotes/${quoteId}`;
	/**
	 * Route for organisation users list.
	 */
	static OrganisationUserList = "/app/admin/users";
	/**
	 * Route for organisation user creation.
	 */
	static OrganisationUserCreate = "/app/admin/users/create";
	/**
	 * Route for organisation user editing
	 * @param userId
	 */
	static OrganisationUserEdit = (userId: string) => `/app/admin/users/${userId}`;
	/**
	 * Route to all transactions view.
	 * @returns {string}
	 * @constructor
	 */
	static AllTransactionsView = "/app/accounting/transactions";

	/**
	 * Route to invoice details view.
	 * @returns {string}
	 * @constructor
	 * @param jobReferenceNumber
	 */
	static InvoiceDetailsView = (jobReferenceNumber: string) => `/app/jobs/${jobReferenceNumber}/invoice`;

	/**
	 * Route to job bill of materials view.
	 * @returns {string}
	 * @constructor
	 * @param jobReferenceNumber
	 */
	static BillOfMaterials = (jobReferenceNumber: string) => `/app/jobs/${jobReferenceNumber}/billofmaterials`;

	/**
	 * Route to detailed job bill of materials(order) view
	 * @returns {string}
	 * @constructor
	 * @param jobReferenceNumber
	 * @param bomId
	 */
	static BillOfMaterialsDetails = (jobReferenceNumber: string,
		bomId: string) => `/app/jobs/${jobReferenceNumber}/billofmaterials/${bomId}`;

	/**
	 * Route to detailed preview of job bill of materials for selected merchant
	 * @returns {string}
	 * @constructor
	 * @param jobReferenceNumber
	 * @param merchantId
	 */
	static BillOfMaterialsPreview = (jobReferenceNumber: string,
		merchantId: string) => `/app/jobs/${jobReferenceNumber}/billofmaterials/merchants/${merchantId}`;

	/**
	 * Route to bill details view.
	 * @param {string} billId
	 * @returns {string}
	 * @constructor
	 */
	static BillDetailsView = (billId: string) => `/app/accounting/bills/${billId}`;

	/**
	 * Route to bills workspace view.
	 * @returns {string}
	 * @constructor
	 */
	static BillWorkspaceView = "/app/accounting/bills_workspace";

	/**
	 * Route to transaction details view.
	 * @returns {string}
	 * @constructor
	 * @param transactionId
	 */
	static TransactionDetailsView = (transactionId: string) => `/app/accounting/transactions/${transactionId}`;

	/**
	 * Route to stripe payout details view.
	 * @returns {string}
	 * @constructor
	 * @param stripePayoutId
	 */
	static StripePayoutDetailsView = (stripePayoutId: string) => `/app/accounting/transactions/stripe_payout/${stripePayoutId}`;

	/**
	 * Route to GoCardless payout details view.
	 * @returns {string}
	 * @constructor
	 * @param goCardlessPayoutId
	 */
	static GoCardlessPayoutDetailsView = (goCardlessPayoutId: string) => `/app/accounting/transactions/gocardless_payout/${goCardlessPayoutId}`;

	/**
	 * Route for edit asset.
	 * @param {string} assetId Asset identifier.
	 */
	static EditAsset = (assetId: string) => `/app/admin/assets/${assetId}`;

	/**
	 * Route for creating asset.
	 */
	static AddAsset = '/app/admin/assets/create';

	/**
	 * Route for assets list.
	 */
	static AssetList = '/app/admin/assets';
}
